/**
 * VehiclesServiceModule — 2026-06-08 Phase 2D wire-up for the canonical
 * Q Manual VG35002 vehicles catalog REST surface.
 *
 * Wraps the briefing-room backend's vehicles-library endpoints (authored
 * in Phase 2C, commit e13519013). Lets the SPA list every vehicle with
 * compact-shape metadata and fetch a single entry's full data (stats,
 * description, Q evaluation, game information, special rules).
 *
 * Routes (per briefing_room_backend/routes/vehicles.py):
 *   GET /api/modules/briefing-room/vehicles               → VehiclesManifest
 *   GET /api/modules/briefing-room/vehicles/{vehicle_id}  → full vehicle JSON | 404
 *
 * Auth: Bearer JWT from localStorage via getAuthHeaders() (mirrors
 * WeaponsServiceModule.ts).
 *
 * Plan reference: docs/20260608/20260608_PRE_COMPACT_18_HANDOFF_*.md §5.1.
 */
import { getAuthHeaders } from './authHeaders';

/** Top-level vehicle category values per the seed schema (3 categories). */
export type VehicleCategory = 'land_vehicle' | 'air_vehicle' | 'water_vehicle';

/** Per-vehicle summary returned by the manifest endpoint. */
export interface VehicleManifestEntry {
  id: string;
  name: string;
  category: VehicleCategory;
  /**
   * Finer-grained grouping for in-tab sub-headers — load-bearing for the
   * SPA's 4-tab drawer that renders subcategory groupings inside each tab.
   */
  subcategory: string | null;
  page: number | null;
}

export interface VehiclesManifest {
  vehicles: VehicleManifestEntry[];
  total: number;
  categories_complete: string[];
  categories_pending: string[];
  schema_version: string;
  source: string;
}

const BASE_PATH = '/api/modules/briefing-room/vehicles';

export class VehiclesServiceModule {
  constructor(
    private readonly fetchFn: typeof fetch = (input, init) => fetch(input, init),
  ) {}

  /** List all vehicles with compact metadata. */
  async list(): Promise<VehiclesManifest> {
    const resp = await this.fetchFn(BASE_PATH, {
      method: 'GET',
      headers: { ...getAuthHeaders() },
    });
    if (!resp.ok) {
      throw new Error(`VehiclesServiceModule: list HTTP ${resp.status}`);
    }
    return (await resp.json()) as VehiclesManifest;
  }

  /**
   * Fetch a single vehicle's full entry. Returns null on 404 so callers
   * can branch on missing-vs-error.
   */
  async get(vehicleId: string): Promise<Record<string, unknown> | null> {
    const resp = await this.fetchFn(
      `${BASE_PATH}/${encodeURIComponent(vehicleId)}`,
      {
        method: 'GET',
        headers: { ...getAuthHeaders() },
      },
    );
    if (resp.status === 404) {
      return null;
    }
    if (!resp.ok) {
      throw new Error(`VehiclesServiceModule: get HTTP ${resp.status}`);
    }
    return (await resp.json()) as Record<string, unknown>;
  }
}
